// A declined charge (HTTP 402, LAWPAY_DECLINED) shows the server error sentence as-is. Delivered 2026-11-01.
// One-time eCheck on Pay Invoice: hosted routing_number and bank_account_number, holder and account type, method "bank", and 8am's authorization sentence beside Submit Payment. A bank:false or bank_unavailable config shows that notice. LawPay stays in test mode. Delivered 2026-11-01.
// LawPay dashboard refunds/voids: payments may include status_label (Refunded, Partially refunded) and refunds[] (amount, method, date, invoices/reverses). Each refund is its own line. Missing fields keep the previous status pill and omit the line. Delivered 2026-11-01.
// pkg198 — link to Payment methods (saved card or bank account, autopay, payment plans) — delivered 2026-11-01
// pkg195 — payment method 'unknown' displays as Unknown on the invoice and the payments list — delivered 2026-10-31
// pkg177 — client Invoices & Payments redesign (D-177a/b): summary cards (balance due · held in trust · last payment), card-framed tables with status pills, invoice rows open a DETAIL MODAL (time entries, expenses, totals, payments applied, Download .docx) rather than expanding the page; Pay now still uses the pkg172a/175 hosted-fields card form; subject_display fallback for legacy blank subjects — delivered 2026-10-04
// pkg176 — date display fix: DATE columns parsed as calendar dates (no local-time shift)
// pkg175 — hosted-fields iframe sizing
// pkg172a — Client portal "Invoices & Payments" (lane 1, D-172b/c/e): card-only hosted-fields pay; eCheck via the firm's emailed LawPay link
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { paymentMethodLabel } from '../utils/paymentMethodLabel';
import { listRefunds, paymentStatusLabel, paymentStatusTone, refundAmount, refundReversesLabel, refundWhen } from '../utils/paymentRefundDisplay';
import { bankUnavailableMessage, echeckAuthorizationFromConfig, onlinePayOffered } from '../utils/echeckAuthorization';
import { lawpayChargeError } from '../utils/lawpayChargeError';
import { formatFirmDate } from '../utils/firmDate';
import { useFirmName } from '../contexts/FirmSettingsContext';

const HF_SRC = 'https://cdn.affinipay.com/hostedfields/1.5.3/fieldGen_1.5.3.js';
const money = n => '$' + (+n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = (s) => formatFirmDate(s);
const F = { fontFamily: 'Arial, sans-serif' };
const SERIF = { fontFamily: 'Georgia, serif' };
const NAVY = '#0F1838';

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,24,56,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflow: 'auto' };
const box = (w) => ({ background: '#fff', borderRadius: 8, width: '100%', maxWidth: w, boxShadow: '0 12px 48px rgba(0,0,0,0.3)', ...F, overflow: 'hidden' });
const card = { background: '#fff', border: '1px solid #e3ddd0', borderRadius: 8, padding: 0, overflow: 'hidden' };
const th = { ...F, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff', background: NAVY, padding: '9px 12px', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { ...F, fontSize: 13, padding: '10px 12px', borderBottom: '1px solid #f0ece3', verticalAlign: 'middle' };
const right = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const hfBox = { border: '1px solid #c9ced6', borderRadius: 4, height: 40, padding: '0 10px', background: '#fff', overflow: 'hidden', position: 'relative', boxSizing: 'border-box' };
const hfInputCss = { 'font-family': 'Arial, sans-serif', 'font-size': '15px', color: '#1a1a1a', border: 'none', outline: 'none', 'box-shadow': 'none', height: '38px', 'line-height': '38px', width: '100%', padding: '0', margin: '0', background: 'transparent' };
const HF_IFRAME_CSS = `
  .lp-hf iframe { display:block !important; width:100% !important; height:38px !important; border:0 !important; margin:0 !important; padding:0 !important; background:transparent !important; }
  .lp-hf { line-height: 0; }
`;

function pill(kind, text) {
  const map = {
    due: ['#fff3e0', '#9a4b00'], partial: ['#e8f1fb', '#1c4e80'], paid: ['#e4f3e8', '#1e6b35'],
    processing: ['#fff3e0', '#9a4b00'], cleared: ['#e4f3e8', '#1e6b35'], reversed: ['#fdecea', '#8a1c1c'],
    refunded: ['#f0f0f0', '#555'], voided: ['#f3f3f3', '#666'], failed: ['#fdecea', '#8a1c1c'],
  };
  const [bg, fg] = map[kind] || ['#eee', '#444'];
  return <span style={{ ...F, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 10, background: bg, color: fg, whiteSpace: 'nowrap' }}>{text}</span>;
}
const invoicePill = s => s === 'paid' ? pill('paid', 'Paid') : s === 'partial' ? pill('partial', 'Partially paid') : pill('due', 'Due');
const CLIENT_PILL = { partial: 'partial', refunded: 'refunded', voided: 'voided', reversed: 'reversed', failed: 'failed', pending: 'processing', cleared: 'cleared' };
function paymentPill(p) {
  const label = paymentStatusLabel(p);
  if (label) return pill(CLIENT_PILL[paymentStatusTone(label, p && p.status)] || 'cleared', label);
  if (p.status === 'reversed') return pill('reversed', 'Reversed');
  if (p.status === 'pending') return pill('processing', 'Processing');
  if (p.status === 'refunded') return pill('refunded', 'Refunded');
  return pill('cleared', 'Cleared');
}
function methodText(method) {
  return paymentMethodLabel(method, String(method || '').replace(/_/g, ' ')) || '—';
}
function refundMoney(amount) {
  if (amount == null) return '—';
  const abs = money(Math.abs(amount));
  return amount < 0 ? abs : `−${abs}`;
}

let hfLoading = null;
function loadHostedFields() {
  if (window.AffiniPay && window.AffiniPay.HostedFields) return Promise.resolve();
  if (hfLoading) return hfLoading;
  hfLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = HF_SRC; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { hfLoading = null; reject(new Error('Could not load the secure payment form. Check your connection and try again.')); };
    document.head.appendChild(s);
  });
  return hfLoading;
}

function ModalHeader({ title, sub, onClose }) {
  return (
    <div style={{ background: NAVY, color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
      <div>
        <div style={{ ...SERIF, fontSize: 18 }}>{title}</div>
        {sub && <div style={{ ...F, fontSize: 12, opacity: 0.8, marginTop: 2 }}>{sub}</div>}
      </div>
      <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
    </div>
  );
}

// ── Invoice detail modal (D-177b) ────────────────────────────────────────────
// pkg186 — backdrop click = mousedown AND click both on the overlay itself.
// A click event fires on the common ancestor of mousedown and mouseup, so a
// drag-select that starts in a field and ends on the backdrop looks like a
// backdrop click to a plain onClick and closed the modal mid-edit.
function useBackdropClose(onClose, enabled = true) {
  const downOnBackdrop = useRef(false);
  return {
    onMouseDown: (e) => { downOnBackdrop.current = e.target === e.currentTarget; },
    onClick: (e) => {
      const close = enabled && downOnBackdrop.current && e.target === e.currentTarget;
      downOnBackdrop.current = false;
      if (close) onClose();
    },
  };
}

function InvoiceModal({ invoice, pay, onClose, onPay }) {
  const backdrop = useBackdropClose(onClose);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [dl, setDl] = useState(false);
  useEffect(() => { api.clientInvoice(invoice.id).then(setD).catch(e => setErr(e.message)); }, [invoice.id]);

  async function download() {
    setDl(true);
    try {
      const blob = await api.clientInvoiceDownload(invoice.id);
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = `Invoice_${invoice.invoice_number}.docx`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setErr('Download failed. Please try again.'); } finally { setDl(false); }
  }

  const sub = invoice.subject_display || invoice.subject;
  const h = { ...F, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#777', margin: '18px 0 6px' };
  const tiny = { ...F, fontSize: 11, color: '#888' };
  return (
    <div style={overlay} {...backdrop}>
      <div style={box(760)}>
        <ModalHeader title={`Invoice #${invoice.invoice_number}`} sub={sub} onClose={onClose} />
        <div style={{ padding: '16px 20px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
            {[['Issued', fmtDate(invoice.issue_date)], ['Due', fmtDate(invoice.due_date)], ['Total', money(invoice.total)], ['Balance', money(invoice.balance_due)]].map(([l, v]) => (
              <div key={l} style={{ background: '#faf8f3', border: '1px solid #ece7db', borderRadius: 6, padding: '8px 10px' }}>
                <div style={tiny}>{l}</div><div style={{ ...F, fontSize: 15, fontWeight: 700 }}>{v}</div>
              </div>
            ))}
          </div>
          {err && <div className="alert alert-error" style={{ marginTop: 12 }}>{err}</div>}
          {!d && !err && <p style={{ ...F, fontSize: 13, color: '#888' }}>Loading…</p>}
          {d && (
            <>
              {d.entries.length > 0 && <>
                <div style={h}>Professional services</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Date</th><th style={th}>Description</th><th style={{ ...th, ...right }}>Hours</th><th style={{ ...th, ...right }}>Rate</th><th style={{ ...th, ...right }}>Amount</th></tr></thead>
                    <tbody>{d.entries.map(e => (
                      <tr key={e.id}><td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</td><td style={td}>{e.description}{e.biller ? <span style={tiny}> · {e.biller}</span> : null}</td><td style={{ ...td, ...right }}>{(+e.hours).toFixed(2)}</td><td style={{ ...td, ...right }}>{money(e.rate)}</td><td style={{ ...td, ...right, fontWeight: 600 }}>{money(e.amount)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              </>}
              {d.expenses.length > 0 && <>
                <div style={h}>Costs &amp; expenses</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Date</th><th style={th}>Description</th><th style={{ ...th, ...right }}>Qty</th><th style={{ ...th, ...right }}>Unit</th><th style={{ ...th, ...right }}>Amount</th></tr></thead>
                    <tbody>{d.expenses.map(x => (
                      <tr key={x.id}><td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(x.date)}</td><td style={td}>{x.description}{x.category ? <span style={tiny}> · {x.category}</span> : null}</td><td style={{ ...td, ...right }}>{+x.quantity}</td><td style={{ ...td, ...right }}>{money(x.unit_price)}</td><td style={{ ...td, ...right, fontWeight: 600 }}>{money(x.amount)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              </>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <table style={{ ...F, fontSize: 13, borderCollapse: 'collapse', minWidth: 280 }}>
                  <tbody>
                    {d.entries.length > 0 && <tr><td style={{ padding: '3px 12px 3px 0', color: '#555' }}>Professional services</td><td style={{ ...right, padding: '3px 0' }}>{money(d.fees_subtotal)}</td></tr>}
                    {d.expenses.length > 0 && <tr><td style={{ padding: '3px 12px 3px 0', color: '#555' }}>Costs &amp; expenses</td><td style={{ ...right, padding: '3px 0' }}>{money(d.expenses_subtotal)}</td></tr>}
                    {+d.discount > 0 && <tr><td style={{ padding: '3px 12px 3px 0', color: '#555' }}>Discount</td><td style={{ ...right, padding: '3px 0' }}>−{money(d.discount)}</td></tr>}
                    {+d.tax > 0 && <tr><td style={{ padding: '3px 12px 3px 0', color: '#555' }}>Tax</td><td style={{ ...right, padding: '3px 0' }}>{money(d.tax)}</td></tr>}
                    <tr><td style={{ padding: '6px 12px 3px 0', fontWeight: 700, borderTop: '1px solid #ddd' }}>Total</td><td style={{ ...right, padding: '6px 0 3px', fontWeight: 700, borderTop: '1px solid #ddd' }}>{money(d.total)}</td></tr>
                    {d.applied.length > 0 && <tr><td style={{ padding: '3px 12px 3px 0', color: '#555' }}>Payments applied</td><td style={{ ...right, padding: '3px 0', color: '#1e6b35' }}>−{money(d.applied.reduce((s, a) => s + (+a.amount || 0), 0))}</td></tr>}
                    <tr><td style={{ padding: '6px 12px 0 0', fontWeight: 700, fontSize: 15 }}>Balance due</td><td style={{ ...right, padding: '6px 0 0', fontWeight: 700, fontSize: 15 }}>{money(d.balance_due)}</td></tr>
                  </tbody>
                </table>
              </div>
              {d.applied.length > 0 && <>
                <div style={h}>Payments applied</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>{d.applied.map(a => (
                    <tr key={a.id}><td style={td}>{fmtDate(a.date)}</td><td style={td}>{paymentMethodLabel(a.method, String(a.method || '').replace('_', ' ')) || '—'}</td><td style={td}>{a.status === 'pending' ? pill('processing', 'Processing') : pill('cleared', 'Cleared')}</td><td style={{ ...td, ...right, fontWeight: 600 }}>{money(a.amount)}</td></tr>
                  ))}</tbody>
                </table>
              </>}
              {d.notes && <><div style={h}>Notes</div><p style={{ ...F, fontSize: 13, whiteSpace: 'pre-wrap', margin: 0 }}>{d.notes}</p></>}
            </>
          )}
          <div className="btn-row" style={{ justifyContent: 'flex-end', marginTop: 20, gap: 8 }}>
            <button className="btn" onClick={download} disabled={dl}>{dl ? 'Preparing…' : 'Download invoice'}</button>
            {onlinePayOffered(pay) && invoice.status !== 'paid' && +invoice.balance_due >= 1 && <button className="btn btn-primary" onClick={() => onPay(invoice)}>Pay {money(invoice.balance_due)}</button>}
            {onlinePayOffered(pay) && invoice.status !== 'paid' && +invoice.balance_due >= 1 && <Link to="/payment-methods" className="btn">Save a card &amp; turn on autopay</Link>}
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function hostedError(ex, fallback) {
  let msg = fallback;
  try {
    const p = window.AffiniPay.HostedFields.parseException(ex);
    msg = (p.messages && p.messages[0]) || p.message || msg;
  } catch (_) { /* library message unavailable */ }
  return msg;
}

// ── Pay modal (pkg172a/175) — card, or one-time eCheck via hosted bank fields ─
function PayModal({ invoice, pay, me, onClose, onPaid }) {
  const portalFirm = useFirmName();
  const [amount, setAmount] = useState((+invoice.balance_due).toFixed(2));
  const [payMethod, setPayMethod] = useState('card');
  const [cfg, setCfg] = useState(null);
  const [name, setName] = useState(me.name || '');
  const [email, setEmail] = useState(me.email || '');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [postal, setPostal] = useState('');
  const [holder, setHolder] = useState('individual');
  const [given, setGiven] = useState('');
  const [surname, setSurname] = useState('');
  const [business, setBusiness] = useState('');
  const [accountType, setAccountType] = useState('CHECKING');
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const hfRef = useRef(null);
  const backdrop = useBackdropClose(onClose, !busy); // pkg186
  const bankNotice = bankUnavailableMessage(cfg);
  const bankOpen = payMethod === 'bank' && !bankNotice;
  const authorizationText = echeckAuthorizationFromConfig(cfg, portalFirm);
  const testMode = (pay && pay.mode === 'test') || (cfg && cfg.mode === 'test');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await api.clientPayConfig();
        if (cancelled) return;
        setCfg(next);
        const cardKey = next.public_key || next.card_public_key;
        if (!cardKey) setPayMethod('bank');
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!cfg) return undefined;
    let cancelled = false;
    setReady(false);
    hfRef.current = null;
    const notice = bankUnavailableMessage(cfg);
    const useBank = payMethod === 'bank';
    if (useBank && notice) {
      setReady(true);
      return undefined;
    }
    const publicKey = useBank ? cfg.bank_public_key : (cfg.public_key || cfg.card_public_key);
    if (!publicKey) {
      if (!cancelled) setErr(useBank ? 'Bank account payments are not available.' : 'Card payments are not available.');
      return undefined;
    }
    (async () => {
      try {
        await loadHostedFields();
        if (cancelled) return;
        const fields = useBank
          ? [
            { selector: '#lp-routing', input: { type: 'routing_number', placeholder: 'Routing number' } },
            { selector: '#lp-account', input: { type: 'bank_account_number', placeholder: 'Account number' } },
          ]
          : [
            { selector: '#lp-card-number', input: { type: 'credit_card_number', placeholder: 'Card number', format: true } },
            { selector: '#lp-card-cvv', input: { type: 'cvv', placeholder: 'CVV' } },
          ];
        hfRef.current = window.AffiniPay.HostedFields.initializeFields({
          publicKey,
          input: { css: hfInputCss },
          fields,
        }, () => {});
        if (!cancelled) setReady(true);
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, [cfg, payMethod]);

  function amountError() {
    const amt = +parseFloat(amount).toFixed(2);
    if (!(amt >= 1)) return 'Minimum online payment is $1.00.';
    if (amt > +invoice.balance_due + 0.001) return `Amount cannot exceed the balance of ${money(invoice.balance_due)}.`;
    return '';
  }

  async function submitCard() {
    if (!name.trim()) return setErr('Name on card is required.');
    if (!/^\d{1,2}$/.test(expMonth) || +expMonth < 1 || +expMonth > 12) return setErr('Enter the expiration month (1–12).');
    if (!/^\d{4}$/.test(expYear)) return setErr('Enter the 4-digit expiration year.');
    if (!postal.trim()) return setErr('Billing ZIP code is required.');
    const amt = +parseFloat(amount).toFixed(2);
    let tok;
    try {
      tok = await hfRef.current.getPaymentToken({ name: name.trim(), postal_code: postal.trim(), exp_month: expMonth, exp_year: expYear, email: email.trim() || undefined });
    } catch (ex) {
      throw new Error(hostedError(ex, 'Please check your card details.'));
    }
    return api.clientPayCharge({ invoice_id: invoice.id, amount: amt, token: tok.id, method: 'card', name: name.trim(), email: email.trim() || undefined });
  }

  async function submitBank() {
    let tokenArgs;
    if (holder === 'business') {
      if (!business.trim()) return setErr('Business name is required.');
      tokenArgs = { type: 'bank', account_holder_type: 'business', name: business.trim(), account_type: accountType };
    } else {
      if (!given.trim() || !surname.trim()) return setErr('First and last name are required.');
      tokenArgs = {
        type: 'bank',
        account_holder_type: 'individual',
        given_name: given.trim(),
        surname: surname.trim(),
        name: `${given.trim()} ${surname.trim()}`,
        account_type: accountType,
      };
    }
    if (!authorizationText || !authorizationText.includes('\u201cSubmit Payment\u201d')) {
      return setErr('The bank authorization text is not available. Refresh and try again.');
    }
    const amt = +parseFloat(amount).toFixed(2);
    let tok;
    try {
      tok = await hfRef.current.getPaymentToken(tokenArgs);
    } catch (ex) {
      throw new Error(hostedError(ex, 'Please check the bank account details.'));
    }
    if (!tok || !tok.id) throw new Error('LawPay did not return a payment token.');
    return api.clientPayCharge({
      invoice_id: invoice.id,
      amount: amt,
      token: tok.id,
      method: 'bank',
      authorization_text: authorizationText,
      name: tokenArgs.name,
      email: email.trim() || undefined,
    });
  }

  async function submit() {
    setErr('');
    const problem = amountError();
    if (problem) return setErr(problem);
    if (payMethod === 'bank' && bankNotice) return setErr(bankNotice);
    setBusy(true);
    try {
      const r = payMethod === 'bank' ? await submitBank() : await submitCard();
      if (r) onPaid(r);
    } catch (e) { setErr(lawpayChargeError(e) || e.message); }
    finally { setBusy(false); }
  }

  const lbl = { ...F, fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 };
  const inp = { ...F, fontSize: 15, width: '100%', boxSizing: 'border-box', height: 40, border: '1px solid #c9ced6', borderRadius: 4, padding: '0 10px' };
  const cardReady = payMethod === 'card' && ready;
  const bankReady = bankOpen && ready;
  return (
    <div style={overlay} {...backdrop}>
      <div style={box(560)}>
        <style>{HF_IFRAME_CSS}</style>
        <ModalHeader title={`Pay Invoice #${invoice.invoice_number}`} sub={`Balance due ${money(invoice.balance_due)}${testMode ? ' · TEST MODE — no real charge' : ''}`} onClose={() => !busy && onClose()} />
        <div style={{ padding: '16px 20px 20px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button type="button" className={`btn btn-sm ${payMethod === 'card' ? 'btn-primary' : ''}`} disabled={busy} onClick={() => { setErr(''); setPayMethod('card'); }}>Card</button>
            <button type="button" className={`btn btn-sm ${payMethod === 'bank' ? 'btn-primary' : ''}`} disabled={busy} onClick={() => { setErr(''); setPayMethod('bank'); }}>eCheck</button>
          </div>
          <p style={{ ...F, fontSize: 12, color: '#666', margin: '0 0 12px' }}>
            {payMethod === 'bank'
              ? 'Routing and account numbers are entered in fields served by LawPay. They never reach this website.'
              : 'Card payments are processed securely by LawPay; your card number never reaches this website.'}
          </p>
          {err && <div className="alert alert-error">{err}</div>}
          {payMethod === 'bank' && bankNotice && <div className="alert alert-error" role="status">{bankNotice}</div>}
          {payMethod === 'card' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Amount</label><input style={inp} type="number" min="1" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Name on card</label><input style={inp} value={name} onChange={e => setName(e.target.value)} autoComplete="cc-name" /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Card number</label><div id="lp-card-number" className="lp-hf" style={hfBox} /></div>
              <div><label style={lbl}>Exp. month</label><input style={inp} value={expMonth} onChange={e => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="MM" inputMode="numeric" autoComplete="cc-exp-month" /></div>
              <div><label style={lbl}>Exp. year</label><input style={inp} value={expYear} onChange={e => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="YYYY" inputMode="numeric" autoComplete="cc-exp-year" /></div>
              <div><label style={lbl}>CVV</label><div id="lp-card-cvv" className="lp-hf" style={hfBox} /></div>
              <div><label style={lbl}>Billing ZIP</label><input style={inp} value={postal} onChange={e => setPostal(e.target.value)} autoComplete="postal-code" /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Receipt email</label><input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
            </div>
          )}
          {bankOpen && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Amount</label><input style={inp} type="number" min="1" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} /></div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Account holder</label>
                <select style={inp} value={holder} onChange={e => setHolder(e.target.value)}>
                  <option value="individual">Individual</option>
                  <option value="business">Business</option>
                </select>
              </div>
              {holder === 'business' ? (
                <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Business name</label><input style={inp} value={business} onChange={e => setBusiness(e.target.value)} /></div>
              ) : (
                <>
                  <div><label style={lbl}>First name</label><input style={inp} value={given} onChange={e => setGiven(e.target.value)} autoComplete="given-name" /></div>
                  <div><label style={lbl}>Last name</label><input style={inp} value={surname} onChange={e => setSurname(e.target.value)} autoComplete="family-name" /></div>
                </>
              )}
              <div>
                <label style={lbl}>Account type</label>
                <select style={inp} value={accountType} onChange={e => setAccountType(e.target.value)}>
                  <option value="CHECKING">Checking</option>
                  <option value="SAVINGS">Savings</option>
                </select>
              </div>
              <div><label style={lbl}>Routing number</label><div id="lp-routing" className="lp-hf" style={hfBox} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Account number</label><div id="lp-account" className="lp-hf" style={hfBox} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Receipt email</label><input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
            </div>
          )}
          {payMethod === 'card' ? (
            <div className="btn-row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={busy || !cardReady}>
                {busy ? <><span className="spinner" />Processing…</> : (cardReady ? `Pay ${money(amount || 0)}` : 'Loading secure form…')}
              </button>
            </div>
          ) : bankOpen ? (
            <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <p style={{ ...F, fontSize: 12, lineHeight: 1.45, color: '#333', margin: 0, flex: '1 1 280px' }}>{authorizationText}</p>
              <div className="btn-row" style={{ margin: 0, justifyContent: 'flex-end' }}>
                <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
                <button className="btn btn-primary" onClick={submit} disabled={busy || !bankReady}>
                  {busy ? <><span className="spinner" />Processing…</> : (bankReady ? 'Submit Payment' : 'Loading secure form…')}
                </button>
              </div>
            </div>
          ) : (
            <div className="btn-row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn" onClick={onClose} disabled={busy}>Close</button>
            </div>
          )}
          <p style={{ ...F, fontSize: 11, color: '#999', margin: '12px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>Secured by <img src="/lawpay-logo.svg" alt="LawPay" style={{ height: 14 }} /></p>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ClientBillingPage() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState({});
  const [err, setErr] = useState('');
  const [viewing, setViewing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.clientPaySummary()); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); api.me().then(setMe).catch(() => {}); }, [load]);

  if (err) return <div className="alert alert-error">{err}</div>;
  if (!data) return <p style={F}>Loading…</p>;

  const due = data.invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + (+i.balance_due || 0), 0);
  const lastPay = data.payments.find(p => p.status !== 'reversed');
  const canPay = (i) => onlinePayOffered(data.pay) && i.status !== 'paid' && +i.balance_due >= 1;
  const h2 = { ...SERIF, fontSize: 16, margin: '24px 0 10px', color: NAVY };

  return (
    <div>
      <h1 style={{ ...SERIF, fontSize: 26, margin: '0 0 4px', color: NAVY }}>Invoices &amp; Payments</h1>
      <p style={{ ...F, fontSize: 13, color: '#666', margin: '0 0 16px' }}>Your invoices, what each one covers, and every payment on file.</p>
      <div style={{ ...card, ...F, fontSize: 14, padding: '12px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span>Save a card or bank account and turn on autopay so new invoices can be charged automatically.</span>
        <Link to="/payment-methods" className="btn btn-sm btn-primary">Payment methods &amp; autopay</Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        {[
          ['Balance due', money(due), due > 0 ? '#B85042' : '#1e6b35'],
          ['Held in trust', money(data.trust_balance || 0), NAVY],
          ['Last payment', lastPay ? `${money(lastPay.amount)} · ${fmtDate(lastPay.payment_date)}` : '—', NAVY],
        ].map(([l, v, c]) => (
          <div key={l} style={{ ...card, padding: '12px 14px' }}>
            <div style={{ ...F, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#888' }}>{l}</div>
            <div style={{ ...F, fontSize: 20, fontWeight: 700, color: c, marginTop: 2 }}>{v}</div>
          </div>
        ))}
      </div>

      {notice && <div className={`alert alert-${notice.kind}`} style={{ marginTop: 14 }}>{notice.text}</div>}

      {data.open_requests.length > 0 && (
        <>
          <h2 style={h2}>Payment requests from your attorney</h2>
          {data.open_requests.map(r => (
            <div key={r.id} style={{ ...card, ...F, fontSize: 13, padding: '10px 14px', background: '#fffbe8', borderColor: '#e2d9b8', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span>{r.purpose === 'retainer' ? 'Retainer deposit' : `Invoice #${r.invoice_number}`} — <strong>{money(r.amount)}</strong> <span style={{ color: '#888' }}>({r.reference})</span></span>
              <a className="btn btn-sm btn-primary" href={r.url} target="_blank" rel="noopener noreferrer">Pay with LawPay ↗</a>
            </div>
          ))}
        </>
      )}

      <h2 style={h2}>Invoices</h2>
      <div style={card}>
        {data.invoices.length === 0 ? <p style={{ ...F, fontSize: 13, color: '#888', padding: 14, margin: 0 }}>No invoices yet.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Invoice</th><th style={th}>Issued</th><th style={th}>Due</th><th style={{ ...th, ...right }}>Total</th><th style={{ ...th, ...right }}>Balance</th><th style={th}>Status</th><th style={th} /></tr></thead>
              <tbody>
                {data.invoices.map(i => (
                  <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => setViewing(i)}>
                    <td style={td}><div style={{ fontWeight: 700 }}>#{i.invoice_number}</div><div style={{ ...F, fontSize: 12, color: '#777' }}>{i.subject_display || i.subject}</div></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(i.issue_date)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(i.due_date)}</td>
                    <td style={{ ...td, ...right }}>{money(i.total)}</td>
                    <td style={{ ...td, ...right, fontWeight: 700 }}>{money(i.balance_due)}</td>
                    <td style={td}>{invoicePill(i.status)}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-sm" onClick={() => setViewing(i)}>View</button>{' '}
                      {canPay(i) && <button className="btn btn-sm btn-primary" onClick={() => { setNotice(null); setPaying(i); }}>Pay now</button>}
                      {canPay(i) && <div style={{ marginTop: 6 }}><Link to="/payment-methods" style={{ fontSize: 12 }}>Save a card &amp; turn on autopay</Link></div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <h2 style={h2}>Payments</h2>
      <div style={card}>
        {data.payments.length === 0 ? <p style={{ ...F, fontSize: 13, color: '#888', padding: 14, margin: 0 }}>No payments recorded.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={{ ...th, ...right }}>Amount</th><th style={th}>Method</th><th style={th}>Applied to</th><th style={th}>Status</th></tr></thead>
              <tbody>
                {data.payments.map((p, pi) => (
                  <React.Fragment key={p.id != null ? p.id : `p-${pi}`}>
                    <tr>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(p.payment_date)}</td>
                      <td style={{ ...td, ...right, fontWeight: 600 }}>{money(p.amount)}{p.deposit_account === 'trust' ? <div style={{ ...F, fontSize: 11, color: '#888', fontWeight: 400 }}>to trust</div> : null}</td>
                      <td style={td}>{methodText(p.method)}{p.source === 'lawpay' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6 }}><img src="/lawpay-logo.svg" alt="LawPay" style={{ height: 11 }} /></span> : null}</td>
                      <td style={td}>{p.invoices || (p.deposit_account === 'trust' ? 'Trust account' : '—')}</td>
                      <td style={td}>{paymentPill(p)}</td>
                    </tr>
                    {listRefunds(p).map((r, ri) => {
                      const when = refundWhen(r);
                      const reverses = refundReversesLabel(r);
                      return (
                        <tr key={r.id != null ? `r-${r.id}` : `r-${pi}-${ri}`} style={{ background: '#faf8f3' }}>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: '#555' }}>{when ? `Refund · ${fmtDate(when)}` : 'Refund'}</td>
                          <td style={{ ...td, ...right, fontWeight: 600, color: '#8a1c1c' }}>{refundMoney(refundAmount(r))}</td>
                          <td style={td}>{methodText(r.method)}</td>
                          <td style={td}>{reverses || '—'}</td>
                          <td style={td}>{pill('refunded', 'Refund')}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewing && <InvoiceModal invoice={viewing} pay={data.pay} onClose={() => setViewing(null)} onPay={(i) => { setViewing(null); setNotice(null); setPaying(i); }} />}
      {paying && (
        <PayModal invoice={paying} pay={data.pay} me={me} onClose={() => setPaying(null)}
          onPaid={(r) => { setPaying(null); setNotice({ kind: 'success', text: `Payment of ${money(r.payment.amount)} accepted on invoice #${r.invoice.invoice_number}.${r.pending_note ? ' ' + r.pending_note : ''}` }); load(); }} />
      )}
    </div>
  );
}
